// Coordinate projection for the MDS perceptual map.
// Port of BrandPerceptialMapping.Mds.ChartView#scaleProjection + checkLineIntersection
// (bpm/mds/chart.coffee). Pure math, no DOM/D3 — border is computed once here instead
// of on every legacy scaleProjection(value) call.

const MARGIN_PERCENT = 15

// d3.scale.linear().domain([d0, d1]).range([r0, r1])
const linearScale = (d0, d1, r0, r1) => value => {
  if (d1 === d0) return r0
  return r0 + ((value - d0) * (r1 - r0)) / (d1 - d0)
}

// d3.max(arr, accessor): max over accessor, skipping null/NaN; undefined if empty
const d3Max = (arr, accessor) => {
  let max
  for (const item of arr) {
    const val = accessor(item)
    if (val == null || Number.isNaN(val)) continue
    if (max === undefined || val > max) max = val
  }
  return max
}

const accumulateMax = (current, arr, accessor) => {
  const found = d3Max(arr, accessor)
  return found === undefined ? current : Math.max(found, current)
}

/**
 * Build the MDS coordinate projection.
 * @param {{ width:number, height:number, axises:Array, points:Array }} params
 * @returns {{ border:{x,y,tan}, scaleX:(v)=>number, scaleY:(v)=>number, project:(v)=>{x,y} }}
 */
export const createProjection = ({ width, height, axises, points }) => {
  const pts = points.map(d => ({ x: d.x, y: d.y }))

  let maxX = 0
  let maxY = 0
  for (const group of [axises, pts]) {
    maxX = accumulateMax(maxX, group, i => Math.abs(i.x))
    maxY = accumulateMax(maxY, group, i => Math.abs(i.y))
  }

  let borderX = 0
  let borderY = 0
  if (maxX >= maxY * (width / height)) {
    borderX = maxX + (maxX * MARGIN_PERCENT) / 100
    borderY = borderX * (height / width)
  } else {
    borderY = maxY + (maxY * MARGIN_PERCENT) / 100
    borderX = borderY * (width / height)
  }

  const scaleX = linearScale(-borderX, borderX, 0, width)
  const scaleY = linearScale(-borderY, borderY, 0, height)

  return {
    border: { x: borderX, y: borderY, tan: Math.tan(borderY) },
    scaleX,
    scaleY,
    project: value => ({ x: scaleX(value), y: scaleY(value) })
  }
}

/**
 * Intersection point of two line segments, or { x: null, y: null } when parallel.
 * Port of the inner checkLineIntersection helper from chart.coffee render().
 */
export const checkLineIntersection = (line1, line2) => {
  const result = { x: null, y: null }

  const denominator = (line2.y2 - line2.y1) * (line1.x2 - line1.x1) - (line2.x2 - line2.x1) * (line1.y2 - line1.y1)
  if (denominator === 0) return result

  const a0 = line1.y1 - line2.y1
  const b0 = line1.x1 - line2.x1
  const numerator1 = (line2.x2 - line2.x1) * a0 - (line2.y2 - line2.y1) * b0
  const a = numerator1 / denominator // legacy also derived an unused `b` here

  result.x = line1.x1 + a * (line1.x2 - line1.x1)
  result.y = line1.y1 + a * (line1.y2 - line1.y1)
  return result
}
